/* iter150AI.2 — Approval audit timeline.
 *
 * Renders the dealer_quote_approvals collection rows for the quote
 * (submit, approve, reject — plus any future actions) as a vertical
 * timeline. Color-coded per action; shows actor, timestamp, recipients
 * (for submit), and the approver's note (for approve/reject).
 *
 * Hidden when the quote has no audit rows yet (fresh draft).
 */
import { useCallback, useEffect, useState } from "react";
import {
  ClockClockwise, ArrowRight, CheckCircle, XCircle,
  PaperPlaneTilt, ArrowsClockwise,
} from "@phosphor-icons/react";
import { fetchQuoteApprovalHistory } from "@/lib/api";


export default function ApprovalHistoryTimeline({ headingId, dealerState }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await fetchQuoteApprovalHistory(headingId);
      setRows(r.items || []);
    } catch (e) {
      // 403 just means the caller isn't an editor/approver — silent.
      if (e?.response?.status !== 403) {
        setErr(e?.response?.data?.detail || e?.message || "Failed");
      }
      setRows([]);
    } finally { setLoading(false); }
  }, [headingId]);

  useEffect(() => { load(); }, [load, dealerState?.status]);

  if (loading) return null;
  if (err)     return null;
  if (rows.length === 0) return null;

  return (
    <section className="bg-white border border-slate-200 rounded-sm p-3 mt-4"
             data-testid="approval-history-timeline">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 inline-flex items-center gap-1.5">
          <ClockClockwise size={12} weight="bold" />
          Approval history
          <span className="text-[9px] font-mono text-slate-500 normal-case tracking-normal">
            ({rows.length} event{rows.length === 1 ? "" : "s"})
          </span>
        </h2>
        <button type="button" onClick={load}
                data-testid="approval-history-refresh"
                className="w-7 h-7 inline-flex items-center justify-center rounded-sm text-slate-400 hover:text-slate-700 hover:bg-slate-100">
          <ArrowsClockwise size={11} weight="bold" />
        </button>
      </div>
      <ol className="space-y-2">
        {rows.map((r, idx) => (
          <ApprovalRow key={idx} idx={idx} row={r} />
        ))}
      </ol>
    </section>
  );
}


function ApprovalRow({ idx, row }) {
  const action = (row.action || "").toLowerCase();
  const style = ({
    submit:  { color: "bg-blue-100 text-blue-700 border-blue-300", label: "Submitted", Icon: PaperPlaneTilt },
    approve: { color: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "Approved",  Icon: CheckCircle },
    reject:  { color: "bg-red-100 text-red-700 border-red-300", label: "Rejected",  Icon: XCircle },
  })[action] || { color: "bg-slate-100 text-slate-700 border-slate-300", label: action || "Event", Icon: ArrowRight };

  const Icon = style.Icon;
  const when = row.created_at ? new Date(row.created_at).toLocaleString() : "—";
  return (
    <li className="flex items-start gap-2"
        data-testid={`approval-history-row-${idx}`}>
      <div className={`shrink-0 w-7 h-7 rounded-full border ${style.color} inline-flex items-center justify-center`}>
        <Icon size={12} weight="bold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs">
          <span className="font-bold uppercase tracking-wider mr-1.5">{style.label}</span>
          <span className="text-slate-700">by <b>{row.actor || "—"}</b></span>
          <span className="text-slate-400 ml-1.5">· {when}</span>
        </div>
        {action === "submit" && Array.isArray(row.recipients) && row.recipients.length > 0 && (
          <div className="text-[11px] text-slate-500 mt-0.5">
            emailed to {row.recipients.length} recipient{row.recipients.length === 1 ? "" : "s"}:
            <span className="font-mono ml-1">{row.recipients.join(", ")}</span>
          </div>
        )}
        {row.note && (
          <div className="text-[11px] text-slate-600 mt-0.5 italic border-l-2 border-slate-300 pl-2">
            "{row.note}"
          </div>
        )}
      </div>
    </li>
  );
}
